/**
 * plugins/card.ts — Itens de grid (.tool-card, .metric-card, .persona-card, .cmd-block, .hier-row)
 *
 * Ferramentas: mover ↑↓, excluir, duplicar.
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, closestInside, moveSibling, directChildOf } from '../types';

export class CardPlugin implements VtbPlugin {
    readonly panelId = 'vtb-card';
    readonly selectorHints = ['.tool-card', '.metric-card', '.persona-card', '.cmd-block', '.hier-row'];

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        // Busca se está dentro de um container que gerencia cards
        const container = closestInside(target, visualEditor, e => 
            e.classList.contains('tool-grid') || 
            e.classList.contains('cards') || 
            e.classList.contains('hierarchy') ||
            e.dataset.vtbId === 'tool-grid' ||
            e.dataset.vtbId === 'grid' ||
            e.dataset.vtbId === 'hierarchy' ||
            e.classList.contains('action-card') // action-card pode ser direto
        );

        if (container) {
            const item = directChildOf(container, target);
            if (item && (
                item.classList.contains('tool-card') || 
                item.classList.contains('metric-card') || 
                item.classList.contains('persona-card') ||
                item.classList.contains('cmd-block') ||
                item.classList.contains('hier-row') ||
                item.dataset.vtbId === 'tool-card' ||
                item.dataset.vtbId === 'card' ||
                item.dataset.vtbId === 'persona-card' ||
                item.dataset.vtbId === 'cmd' ||
                item.dataset.vtbId === 'hier-row'
            )) {
                return { type: this.panelId, label: 'Item / Card', el: item, container };
            }
        }

        // Caso especial: persona-card ou cmd-block solto
        const standalone = closestInside(target, visualEditor, e => 
            e.classList.contains('persona-card') || 
            e.classList.contains('cmd-block') ||
            e.dataset.vtbId === 'persona-card' ||
            e.dataset.vtbId === 'cmd'
        );
        if (standalone) {
            return { type: this.panelId, label: 'Card', el: standalone, container: standalone.parentElement ?? undefined };
        }

        return null;
    }

    activate(ctx: SmartCtx, registry: IVtbRegistry): void {
        const chip = registry.q('vtb-card .vtb-chip');
        if (chip) {
            let label = '🃏 Card';
            if (ctx.el.classList.contains('hier-row')) label = '🌳 Nodo';
            if (ctx.el.classList.contains('cmd-block')) label = '💻 Comando';
            chip.textContent = label;
        }
    }

    setup(registry: IVtbRegistry): void {
        registry.q('vtb-card-up')?.addEventListener('click',   () => this._move(registry, -1));
        registry.q('vtb-card-down')?.addEventListener('click', () => this._move(registry,  1));
        registry.q('vtb-card-del')?.addEventListener('click',  () => this._delete(registry));
        registry.q('vtb-card-dup')?.addEventListener('click',  () => this._duplicate(registry));
    }

    private _move(registry: IVtbRegistry, dir: -1 | 1): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        moveSibling(ctx.el, dir);
        registry.sync();
    }

    private _delete(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        ctx.el.remove();
        registry.clearSelection();
        registry.sync();
    }

    private _duplicate(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const clone = ctx.el.cloneNode(true) as HTMLElement;
        ctx.el.parentElement?.insertBefore(clone, ctx.el.nextSibling);
        registry.sync();
        registry.selectEl(clone);
    }
}
